import React, { useEffect, useState } from 'react';
import { Checkbox } from '@material-ui/core';
import { Autocomplete } from '@material-ui/lab';
import CheckBoxOutlineBlankIcon from '@material-ui/icons/CheckBoxOutlineBlank';
import CheckBoxIcon from '@material-ui/icons/CheckBox';
import { useApolloClient } from '@apollo/client';
import { useTranslation } from 'react-i18next';
import { isEmpty, keyBy } from 'lodash/fp';
import { Category } from '../../../../../server-types';
import { GET_CATEGORY_OPTIONS } from '../../../graphql';
import { useProductQueryContext } from '../../hooks';
import { Options } from '../../../../../components/forms/fields/types';
import { useDebounce } from '../../../../../utils';
import { TableHeadTextField } from '../../../../../ui-kit';

const icon = <CheckBoxOutlineBlankIcon fontSize="small" />;
const checkedIcon = <CheckBoxIcon fontSize="small" />;

const OptionComponent = ({ option, selected }: { selected: boolean; option: Options}) => (
    <React.Fragment>
        <Checkbox
            icon={icon}
            color='primary'
            checkedIcon={checkedIcon}
            style={{ marginRight: 8 }}
            checked={selected}
        />
        {option.name}
    </React.Fragment>
);

const renderTags = (selectedOptions: Options[], emptyOption?: string) => {
    const optionsLength = selectedOptions.length - 1;
    const firstOptionName = selectedOptions[0]?.name;
    if(optionsLength === 0) {
        return firstOptionName;
    }
    if(optionsLength > 0) {
        return `${firstOptionName} +${optionsLength}`;
    }

    return emptyOption;
};

const CategoryAutocompleteSelect = () => {
    const { t } = useTranslation();
    const [open, setOpen] = useState(false);
    const { categories: contextCategories, setCategories: setContextCategories, setPage } = useProductQueryContext();
    const [categories, setCategories] = useState(contextCategories);

    const debouncedCategoryIds = useDebounce(categories);

    useEffect(() => {
        setContextCategories(debouncedCategoryIds);
        setPage(0);
    }, [debouncedCategoryIds, setContextCategories, setPage]);

    const handleCategoriesChange = (_e: unknown, val: Options[]) => {
        const ids = val.map(({ id }) => id);
        setCategories(ids);
    };

    const client = useApolloClient();
    const cachedCategories = client.readQuery<{ categories: Category[] }>({ query: GET_CATEGORY_OPTIONS });
    const allCategories = cachedCategories ? cachedCategories.categories : [];
    const keyedCategories = keyBy('id', allCategories);
    const selectedCategories = categories.map(id => keyedCategories[id]);
    const placeholder = t('Category');
    return (
        <Autocomplete
            open={open}
            fullWidth
            onOpen={() => setOpen(true)}
            onClose={() => setOpen(false)}
            options={allCategories}
            value={selectedCategories}
            onChange={handleCategoriesChange}
            getOptionLabel={({ name }) => name}
            renderOption={(option, { selected }) => (
                <OptionComponent
                    option={option}
                    selected={selected}
                />
            )}
            renderTags={values => renderTags(values)}
            multiple
            size='small'
            disableCloseOnSelect
            disableListWrap
            renderInput={({ InputProps: passedInputProps, ...params }) => (
                <TableHeadTextField
                    InputProps={{
                        ...passedInputProps,
                        disableUnderline: true,
                    }}
                    margin='dense'
                    placeholder={!isEmpty(selectedCategories) ? undefined : placeholder}
                    {...params}
                />
            )}
        />
    );
};

export default CategoryAutocompleteSelect;
